package com.havn.app.core.designsystem

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.ui.theme.LocalHavnThemeTokens

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HavnPullToRefreshBox(
    isRefreshing: Boolean,
    onRefresh: () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable BoxScope.() -> Unit
) {
    if (havnIsWebShell) {
        Box(modifier = modifier, content = content)
    } else {
        PullToRefreshBox(
            isRefreshing = isRefreshing,
            onRefresh = onRefresh,
            modifier = modifier,
            content = content
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun HavnScreenFrame(
    eyebrow: String,
    title: String,
    subtitle: String,
    metrics: List<HavnMetric> = emptyList(),
    backLabel: String? = null,
    onBack: (() -> Unit)? = null,
    isRefreshing: Boolean = false,
    onRefresh: (() -> Unit)? = null,
    compactHeader: Boolean = false,
    content: @Composable () -> Unit
) {
    val strings = LocalHavnStrings.current
    val tokens = LocalHavnThemeTokens.current
    val scrollState = rememberScrollState()
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    HavnPullToRefreshBox(
        isRefreshing = isRefreshing,
        onRefresh = {
            if (gestureStartedAtTop) {
                onRefresh?.invoke()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(scrollState) { gestureStartedAtTop = it }
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(tokens.gradientStart, tokens.background, tokens.gradientEnd)
                    )
                )
                .verticalScroll(scrollState)
                .padding(horizontal = 22.dp, vertical = 24.dp)
        ) {
            if (compactHeader && onBack == null) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = title,
                        modifier = Modifier.weight(1f),
                        color = tokens.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                }
            } else {
                if (onBack != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable(onClick = onBack)
                        .padding(vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    HavnIcon(
                        icon = HavnIconKey.Back,
                        contentDescription = backLabel ?: strings.back,
                        tint = tokens.primaryAccent
                    )
                    if (backLabel?.isNotBlank() != false) {
                        Spacer(Modifier.width(6.dp))
                        Text(
                            text = backLabel ?: strings.back,
                            modifier = Modifier.weight(1f),
                            color = tokens.primaryAccent,
                            style = MaterialTheme.typography.labelLarge,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
                Spacer(Modifier.height(12.dp))
                }
                if (eyebrow.isNotBlank()) {
                    Text(
                        text = eyebrow,
                        color = tokens.primaryAccent,
                        style = MaterialTheme.typography.labelLarge
                    )
                    Spacer(Modifier.height(8.dp))
                }
                Text(
                    text = title,
                    color = tokens.primaryText,
                    style = HavnTopHeaderTextStyle,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                if (subtitle.isNotBlank()) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = subtitle,
                        color = tokens.secondaryText,
                        style = MaterialTheme.typography.bodyLarge
                    )
                }
            }

            if (metrics.isNotEmpty()) {
                Spacer(Modifier.height(24.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    metrics.forEach { metric ->
                        HavnMetricCard(metric, Modifier.weight(1f))
                    }
                }
            }

            Spacer(Modifier.height(24.dp))
            content()
            Spacer(Modifier.height(16.dp))
        }
    }
}

@Composable
fun HavnMetricCard(metric: HavnMetric, modifier: Modifier = Modifier) {
    val tokens = LocalHavnThemeTokens.current
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(containerColor = tokens.surface),
        border = BorderStroke(1.dp, tokens.border)
    ) {
        Column(Modifier.padding(14.dp)) {
            Text(metric.label, color = tokens.secondaryText, style = MaterialTheme.typography.labelLarge)
            Spacer(Modifier.height(6.dp))
            Text(metric.value, color = tokens.primaryText, style = MaterialTheme.typography.headlineMedium)
            Spacer(Modifier.height(2.dp))
            Text(metric.delta, color = tokens.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
fun HavnActionPanel(title: String, body: String, action: String, modifier: Modifier = Modifier) {
    val tokens = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .fillMaxWidth()
            .border(1.dp, tokens.primaryAccent, MaterialTheme.shapes.large),
        color = tokens.surface,
        shape = MaterialTheme.shapes.large
    ) {
        Row(
            modifier = Modifier.padding(18.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(Modifier.weight(1f)) {
                Text(title, color = tokens.primaryText, style = MaterialTheme.typography.titleLarge)
                Spacer(Modifier.height(6.dp))
                Text(body, color = tokens.secondaryText, style = MaterialTheme.typography.bodyLarge)
            }
            Spacer(Modifier.width(16.dp))
            Text(action, color = tokens.primaryAccent, style = MaterialTheme.typography.labelLarge)
        }
    }
}

data class HavnMetric(
    val label: String,
    val value: String,
    val delta: String
)

private val ColorStopGraphite = HavnGraphite.copy(alpha = 0.92f)
